import React, { useCallback, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import LinearGradient from 'utils/packages/linearGradient';
import IconPlus from 'design-system/icons/IconPlus';
import { SettingsItem } from 'design-system/SettingsItem';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { selectNormalizedCategories } from 'reducers/selectors';
import { isWeb } from '../../../../constants';
import EditCategoryListVerticalRow from './EditCategoryVerticalListRow';
const keyExtractor = (item) => item.id;
const ManageSubcategoriesList = () => {
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const styles = useStyles(styleSet);
    const onPressCategory = useRequiredBenefitCallbackWithSpaces(useCallback((category) => {
        navigation.navigateDeprecated('CustomCategoryEdit', { category });
    }, []), 'customCategories');
    const data = useMemo(() => Object.values(normalizedCategories).reduce((acc, curr, index) => {
        const subcategoriesLength = curr.subCategoryIds?.length;
        if (!subcategoriesLength || !curr.subCategoryIds)
            return acc;
        return [
            ...acc,
            {
                ...curr,
                index,
                subcategoriesLength,
            },
            ...curr.subCategoryIds.map((id, index) => ({
                ...normalizedCategories[id],
                index: index + 1,
                subcategoriesLength: subcategoriesLength + 1,
            })),
        ];
    }, []), [normalizedCategories]);
    const renderItem = useCallback(({ item }) => {
        const { index, subcategoriesLength, displayName } = item;
        if (!item.parentCategoryId) {
            return (<FlatListItemCard index={0} itemsLength={subcategoriesLength + 1} style={[styles.listItem, styles.categoryTitle]}>
            <Text Text-16>{displayName}</Text>
          </FlatListItemCard>);
        }
        const isFirst = index === 0;
        const isLast = index === subcategoriesLength - 1;
        return (<FlatListItemCard index={index + 1} itemsLength={subcategoriesLength + 1} style={[
                styles.listItem,
                isFirst && styles.firstItem,
                isLast && styles.lastItem,
            ]}>
          <EditCategoryListVerticalRow category={item} onPress={onPressCategory} showEdit/>
        </FlatListItemCard>);
    }, [
        onPressCategory,
        styles.categoryTitle,
        styles.firstItem,
        styles.lastItem,
        styles.listItem,
    ]);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const ListHeaderComponent = useMemo(() => (<TouchableBounceCard onDark onPress={() => navigation.navigateDeprecated('CreateCustomCategory')} noMarginTop>
        <SettingsItem style={styles.addButton} title="Add new category" disabled renderIcon={() => (<LinearGradient colors={[colors.background.dark, colors.background.dark]} style={styles.addIcon}>
              <IconPlus color={colors.elements.brand}/>
            </LinearGradient>)}/>
      </TouchableBounceCard>), [
        colors.background.dark,
        colors.elements.brand,
        styles.addButton,
        styles.addIcon,
    ]);
    const ListFooterComponent = useMemo(() => (<InfoCard>
        <Text TextThin-14 Secondary>
          Default categories can’t be edited, but you can create unlimited
          custom categories.
        </Text>
      </InfoCard>), []);
    return (<Animated.FlatList scrollEnabled={false} data={data} renderItem={renderItem} keyExtractor={keyExtractor} contentContainerStyle={styles.container} ListHeaderComponent={ListHeaderComponent} ListFooterComponent={ListFooterComponent}/>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(16),
    },
    listItem: {
        backgroundColor: colors.cards.onDark,
    },
    addIcon: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(44),
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    firstItem: {
        ...(isWeb && {
            paddingTop: rem(8),
        }),
    },
    lastItem: {
        marginBottom: rem(16),
    },
    categoryTitle: {
        padding: rem(16),
        borderBottomWidth: 1,
        borderColor: colors.borders.divider,
        ...(isWeb && {
            paddingTop: rem(16),
            paddingHorizontal: rem(24),
        }),
    },
    addButton: {
        marginHorizontal: rem(16),
    },
}));
export default ManageSubcategoriesList;
